import styled, { css } from 'styled-components';

import { ColorName } from 'types';

import { mediaMinWidth } from 'themes/mixins/mediaQueries';
import { pickColor } from 'themes/helpers/pickColor';
import { getFontScale } from 'themes/mixins/getFontScale';

const Row = styled.div`
  margin-top: 2rem;
`;

const Tiles = styled.div<{ $extended?: boolean }>`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.6rem;
  margin-top: 1.2rem;

  ${mediaMinWidth.md(css`
    grid-template-columns: repeat(2, 1fr);
  `)}

  /*
   * Extended variant (Statistics dashboard, 7 tiles). The default four-tile
   * layout above is left completely untouched — this block only applies when
   * the caller opts in AND the extra tiles are present, so the Orders page and
   * any old-backend render keep their exact grid. auto-fit/minmax lays 7 tiles
   * out cleanly from mobile to desktop while keeping each tile a comparable
   * width to the four-column layout.
   */
  ${({ $extended }) =>
    $extended &&
    css`
      grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
    `}
`;

const Tile = styled.div<{ $headerColor?: ColorName }>`
  /* The value below sizes itself to this width (cqi units). */
  container-type: inline-size;
  min-width: 0;
  padding: 1.6rem 2rem;

  background-color: ${pickColor('gray')};
  border-left: 4px solid
    ${({ $headerColor }) => pickColor($headerColor || 'colmarBlue')};
  border-radius: 0.5rem;
`;

const Value = styled.div<{ $headerColor?: ColorName }>`
  ${getFontScale('xl')};
  /*
   * A large amount ("1.330.658,06") is one unbreakable word wider than a narrow
   * tile: shrink the font to the tile's width, never above the scale's size.
   * Browsers without container units keep the size above and break the word
   * instead of letting it run out of the tile.
   */
  font-size: min(${({ theme }) => theme.fontScales.xl[0]}, 11cqi);
  overflow-wrap: anywhere;

  color: ${({ $headerColor }) => pickColor($headerColor || 'colmarBlue')};
  font-weight: 700;
`;

const Label = styled.div`
  ${getFontScale('xs')};
  margin-top: 0.4rem;

  color: ${pickColor('grayDarkSecondary')};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export { Row, Tiles, Tile, Value, Label };
